import React, { useState, useRef, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  Button,
  Label,
  SpinnerLoader,
  Toastify,
  DatePicker,
  SingleSelect,
} from 'lift-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown } from 'lucide-react';
import { useDeactivateConfiguration } from '../../hooks';
import type { ReportConfiguration } from '../../types';
import { transformToUI } from '../../utils/valueTransformations';

// Custom Time Input with Dropdown
interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  validate: (value: string) => boolean;
}

const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, options, placeholder, validate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (val === '' || validate(val)) {
      onChange(val);
    }
  };

  const handleBlur = () => {
    if (value && value.length === 1) {
      onChange(value.padStart(2, '0'));
    }
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-[50px] p-2 border border-r-0 border-gray-300 rounded-l-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          maxLength={2}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="px-1 border border-gray-300 rounded-r-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <ChevronDown className="h-4 w-4 text-gray-500" />
        </button>
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-[70px] max-h-[200px] overflow-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {options.map((option) => (
            <div
              key={option}
              onClick={() => handleSelect(option)}
              className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-primary hover:text-primary-foreground ${
                value === option ? 'bg-primary text-primary-foreground' : ''
              }`}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface DeactivationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  config: ReportConfiguration;
  brandId: number;
  brandName?: string;
  onSuccess: () => void;
}

const DeactivationDialog: React.FC<DeactivationDialogProps> = ({
  isOpen,
  onClose,
  config,
  brandId,
  brandName,
  onSuccess,
}) => {
  // Check if there's already a future deactivation scheduled
  const now = new Date();
  const existingDeactivationDate = config.deactivation_at ? new Date(config.deactivation_at) : null;
  const hasExistingFutureDeactivation = existingDeactivationDate ? existingDeactivationDate > now : false;

  // Helper to extract time from a date
  const getTimeFromDate = (date: Date) => {
    const hour24 = date.getHours();
    const hour12 = hour24 % 12 || 12;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    return {
      hour: String(hour12).padStart(2, '0'),
      minute: String(date.getMinutes()).padStart(2, '0'),
      period,
    };
  };

  // Default to current time + 1 hour, rounded to nearest 15 min
  const getDefaultTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    const minutes = Math.ceil(now.getMinutes() / 15) * 15;
    const hour24 = now.getHours();
    const hour12 = hour24 % 12 || 12;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    return {
      hour: String(hour12).padStart(2, '0'),
      minute: String(minutes % 60).padStart(2, '0'),
      period,
    };
  };

  // Initialize state based on whether there's an existing deactivation date
  const initialTime = hasExistingFutureDeactivation && existingDeactivationDate
    ? getTimeFromDate(existingDeactivationDate)
    : getDefaultTime();

  // If editing existing, don't show checkbox - go straight to date picker
  const [deactivateNow, setDeactivateNow] = useState(!hasExistingFutureDeactivation);
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(
    hasExistingFutureDeactivation && existingDeactivationDate ? existingDeactivationDate : undefined
  );
  
  const [selectedHour, setSelectedHour] = useState(initialTime.hour);
  const [selectedMinute, setSelectedMinute] = useState(initialTime.minute);
  const [selectedPeriod, setSelectedPeriod] = useState(initialTime.period);

  // Generate options for dropdowns
  const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  // Validation functions
  const validateHour = (val: string) => {
    const num = parseInt(val, 10);
    return num >= 1 && num <= 12;
  };

  const validateMinute = (val: string) => {
    const num = parseInt(val, 10);
    return num >= 0 && num <= 59;
  };

  // Convert 12-hour to 24-hour format for submission
  const getScheduledTime = () => {
    let hour24 = parseInt(selectedHour, 10);
    if (selectedPeriod === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (selectedPeriod === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    return `${String(hour24).padStart(2, '0')}:${selectedMinute}`;
  };

  const { mutate: deactivate, isPending } = useDeactivateConfiguration();

  const handleSubmit = () => {
    if (!deactivateNow) {
      if (!scheduledDate) {
        Toastify('error', 'Please select a deactivation date');
        return;
      }
      
      if (!selectedHour || !selectedMinute || !selectedPeriod) {
        Toastify('error', 'Please select a deactivation time');
        return;
      }

      // Combine date and time into ISO 8601 format
      const scheduledTime = getScheduledTime();
      const [hours, minutes] = scheduledTime.split(':');
      const combinedDateTime = new Date(scheduledDate);
      combinedDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

      // Validate future date/time
      const now = new Date();
      if (combinedDateTime <= now) {
        Toastify('error', 'Deactivation date and time must be in the future');
        return;
      }

      // Convert to ISO 8601 format (UTC)
      const deactivationAtISO = combinedDateTime.toISOString();

      deactivate(
        {
          id: config.id,
          brand_id: brandId,
          brand_name: brandName,
          deactivate_now: false,
          deactivation_at: deactivationAtISO,
        },
        {
          onSuccess: () => {
            Toastify('success', hasExistingFutureDeactivation 
              ? 'Deactivation schedule updated successfully' 
              : 'Deactivation scheduled successfully');
            onSuccess();
          },
          onError: (error: Error) => {
            Toastify('error', error.message || 'Failed to schedule deactivation');
          },
        }
      );
    } else {
      deactivate(
        {
          id: config.id,
          brand_id: brandId,
          brand_name: brandName,
          deactivate_now: true,
          deactivation_at: undefined,
        },
        {
          onSuccess: () => {
            Toastify('success', 'Configuration deactivated successfully');
            onSuccess();
          },
          onError: (error: Error) => {
            Toastify('error', error.message || 'Failed to deactivate configuration');
          },
        }
      );
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Deactivate Configuration</SheetTitle>
          <SheetDescription>
            Deactivating this configuration will stop data fetches for this combination.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {/* Configuration Summary */}
          <div className="p-3 bg-gray-50 rounded-md space-y-1 text-sm">
            <div><strong>Platform:</strong> {transformToUI(config.platform)}</div>
            <div><strong>Report Type:</strong> {transformToUI(config.report_type)}</div>
            <div><strong>Granularity:</strong> {transformToUI(config.granularity_level)}</div>
            {config.cadence !== 'n/a' && (
              <div><strong>Cadence:</strong> {transformToUI(config.cadence)}</div>
            )}
            {config.metric_type !== 'n/a' && (
              <div><strong>Metric Type:</strong> {transformToUI(config.metric_type)}</div>
            )}
          </div>

          {/* Deactivate Now Checkbox - only show if NOT editing existing scheduled deactivation */}
          {!hasExistingFutureDeactivation && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="deactivate-now"
                checked={deactivateNow}
                onCheckedChange={(checked) => {
                  setDeactivateNow(checked as boolean);
                  if (checked) {
                    setScheduledDate(undefined);
                    const newDefault = getDefaultTime();
                    setSelectedHour(newDefault.hour);
                    setSelectedMinute(newDefault.minute);
                    setSelectedPeriod(newDefault.period);
                  }
                }}
              />
              <Label htmlFor="deactivate-now" className="cursor-pointer">
                Deactivate Now
              </Label>
            </div>
          )}

          {/* Info message when editing existing scheduled deactivation */}
          {hasExistingFutureDeactivation && (
            <div className="p-3 bg-blue-50 rounded-md text-sm text-blue-900">
              Update the scheduled deactivation date and time below.
            </div>
          )}

          {/* Date and Time Selection (when not deactivating now OR editing existing) */}
          {(!deactivateNow || hasExistingFutureDeactivation) && (
            <div className="space-y-3">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Deactivation Date *</Label>
                  <DatePicker
                    value={scheduledDate}
                    onChange={(date: Date) => setScheduledDate(date)}
                    placeholder="Select date"
                    disabled={false}
                    className="w-full"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label>Time *</Label>
                  <div className="flex items-center gap-2">
                    {/* Hour Input with Dropdown */}
                    <TimeInput
                      value={selectedHour}
                      onChange={setSelectedHour}
                      options={hourOptions}
                      placeholder="HH"
                      validate={validateHour}
                    />
                    <span className="text-lg font-medium">:</span>
                    {/* Minute Input with Dropdown */}
                    <TimeInput
                      value={selectedMinute}
                      onChange={setSelectedMinute}
                      options={minuteOptions}
                      placeholder="MM"
                      validate={validateMinute}
                    />
                    {/* AM/PM Select */}
                    <SingleSelect
                      options={[
                        { label: 'AM', value: 'AM' },
                        { label: 'PM', value: 'PM' },
                      ]}
                      defaultValue={selectedPeriod}
                      onChange={setSelectedPeriod}
                      placeholder="AM"
                      containerClassName="w-[120px]"
                    />
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-gray-500">
                The configuration will remain active until this date and time (local timezone).
              </p>
            </div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <SpinnerLoader className="h-4 w-4 mr-2" />
                Processing...
              </>
            ) : hasExistingFutureDeactivation ? (
              'Update Schedule'
            ) : deactivateNow ? (
              'Deactivate Now'
            ) : (
              'Schedule Deactivation'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default DeactivationDialog;
